import Pagination from '@/components/Pagination';
import { Button } from '@/components/ui/button';
import api from '@/lib/axiosInstance';
import Helper from '@/lib/helper'
import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { Edit, Trash } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Spinner } from '@/components/ui/shadcn-io/spinner';
import type { AgentClaims } from '@/types/AgentClaims';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog"

const Claims = () => {
    const [claims, setClaims] = useState<AgentClaims[] | null>(null);
    const [page, setPage] = useState<number>(1);
    const [length] = useState<number>(10);
    const [total, setTotal] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
    const [selectedClaimId, setSelectedClaimId] = useState<string | null>(null);
    const [deleting, setDeleting] = useState<boolean>(false);

    const statusMessage: Record<number, string> = {
        0: 'Pending verification by admin.',
        1: 'Pending verification by Tracy',
        2: 'Pending verification by Norman',
        3: 'Approved',
        97: 'Rejected By Super Admin',
        98: 'Rejected By Master Admin',
        99: 'Rejected By Director',
    };

    const statusClass: Record<number, string> = {
        0: 'bg-blue-100 border-blue-300 text-blue-600',
        1: 'bg-gray-100 border-gray-300 text-gray-600',
        2: 'bg-yellow-100 border-yellow-300 text-yellow-600',
        3: 'bg-green-100 border-green-300 text-green-600',
        97: 'bg-red-100 border-red-300 text-red-600',
        98: 'bg-red-100 border-red-300 text-red-600',
        99: 'bg-red-100 border-red-300 text-red-600'
    };

    const getClaims = async () => {
        try {
            setLoading(true);
            const response = await api.get(`/me/claims`, {
                params: { page, length },
            });
            if (!response.data.status) {
                throw new Error(response.data.error);
            }
            setTotal(response.data.total);
            setClaims(response.data.claims);
        } catch (err) {
            toast.error(Helper.errorHandler(err));
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteClick = (claimId: string) => {
        setSelectedClaimId(claimId);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!selectedClaimId) return;
        try {
            setDeleting(true);
            const response = await api.delete(`/me/claims/${selectedClaimId}`);
            if (!response.data.status) throw new Error(response.data.error);
            toast.success('Claim deleted successfully');
            // If we just deleted the last item on this page, go back one page
            const remainingOnPage = (claims?.length ?? 1) - 1;
            if (remainingOnPage === 0 && page > 1) {
                setPage(page - 1); // useEffect will refetch
            } else {
                getClaims();
            }
        } catch (err) {
            toast.error(Helper.errorHandler(err));
        } finally {
            setDeleting(false);
            setDeleteDialogOpen(false);
            setSelectedClaimId(null);
        }
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setSelectedClaimId(null);
    };

    // Re-fetch whenever page changes
    useEffect(() => {
        getClaims();
    }, [page]);

    return (
        <div>
            <div className='mb-5 flex justify-between items-center'>
                <h5 className='h5 uppercase bold'>Claims</h5>
            </div>
            <div>
                <Table className='mt-5'>
                    <TableHeader>
                        <TableRow>
                            <TableHead className="w-[100px]">No</TableHead>
                            <TableHead>Address</TableHead>
                            <TableHead>Amount</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Submitted date</TableHead>
                            <TableHead>Action</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={6}>
                                    <div className='flex justify-center py-4'>
                                        <Spinner />
                                    </div>
                                </TableCell>
                            </TableRow>
                        ) : claims && claims.length > 0 ? (
                            claims.map((claim, index) => (
                                <TableRow key={claim.id}>
                                    <TableCell>
                                        {(page - 1) * length + (index + 1)}
                                    </TableCell>
                                    <TableCell>{claim.propertyAddress}</TableCell>
                                    <TableCell>
                                        RM{Helper.formatWithDecimal(Number(claim.commAmount))}
                                    </TableCell>
                                    <TableCell>
                                        <Badge className={`${statusClass[claim.verificationStage]}`}>
                                            {statusMessage[claim.verificationStage]}
                                        </Badge>
                                    </TableCell>
                                    <TableCell>
                                        {Helper.formatDateTime(claim.createdAt)}
                                    </TableCell>
                                    <TableCell>
                                        <div className='flex gap-1'>
                                            <Button asChild
                                                type='button'
                                                size={'sm'}
                                                variant={'outline'}
>
                                                <Link to={`/claims/edit/${claim.id}`}>
                                                    <Edit color='black' />
                                                </Link>
                                            </Button>
                                            {(claim.claimed !== 1 && claim.verificationStage < 3) && (
                                                <Button
                                                    type='button'
                                                    size={'sm'}
                                                    variant={'destructive'}

                                                    onClick={() => handleDeleteClick(claim.id)}>
                                                    <Trash />
                                                </Button>
                                            )}
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={6} className="text-center py-4">
                                    No claims found.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>
            <div className='flex justify-end'>
                <Pagination
                    totalPage={Math.ceil(total / length)}
                    currentPage={page}
                    onPageChange={setPage}
                />
            </div>

            <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This action cannot be undone. This will permanently delete the claim from the system.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel
                            className='cursor-pointer'
                            onClick={handleDeleteCancel}
                            disabled={deleting}>
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                            className='cursor-pointer bg-destructive hover:bg-destructive/90'
                            onClick={handleDeleteConfirm}
                            disabled={deleting}>
                            {deleting ? (
                                <><Spinner className="mr-2" />Deleting...</>
                            ) : 'Delete'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

export default Claims;
